/**
 * useMessageMutations Hook
 *
 * Mutations for sending feedback and pastor messages using URQL.
 * Replaces legacy graphql() calls in FeedbackPage and PastorPage.
 */

import { useCallback } from "react";
import { useMutation } from "urql";
import { extractGraphQLErrorMessage } from "@/lib/graphql-utils";
import { errorTracking } from "@/lib/error-tracking";

// =============================================================================
// MUTATION DOCUMENTS
// =============================================================================

const CreateFeedbackDocument = `
  mutation CreateFeedback($text: String!, $authorName: String) {
    createFeedback(text: $text, authorName: $authorName) {
      id
      createdAt
    }
  }
`;

const CreatePastorMessageDocument = `
  mutation CreatePastorMessage($text: String!, $authorName: String) {
    createPastorMessage(text: $text, authorName: $authorName) {
      id
      createdAt
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

interface MessageInput {
  text: string;
  authorName?: string;
}

interface MessageResult {
  id: string;
  createdAt: string;
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Send feedback message.
 */
export function useSendFeedback() {
  const [result, executeMutation] = useMutation<
    { createFeedback: MessageResult },
    MessageInput
  >(CreateFeedbackDocument);

  const sendFeedback = useCallback(
    async (input: MessageInput): Promise<MessageResult> => {
      const response = await executeMutation(input);

      if (response.error) {
        const message = extractGraphQLErrorMessage(
          response.error,
          "Не удалось отправить сообщение"
        );
        errorTracking.captureError(new Error(message), {
          tags: { feature: "feedback", action: "send" },
        });
        throw new Error(message);
      }

      if (!response.data?.createFeedback) {
        throw new Error("Не удалось отправить сообщение");
      }

      errorTracking.addBreadcrumb({
        category: "feedback",
        message: "Feedback sent",
        data: { messageId: response.data.createFeedback.id },
      });

      return response.data.createFeedback;
    },
    [executeMutation]
  );

  return {
    sendFeedback,
    isLoading: result.fetching,
    error: result.error,
  };
}

/**
 * Send message to pastor.
 */
export function useSendPastorMessage() {
  const [result, executeMutation] = useMutation<
    { createPastorMessage: MessageResult },
    MessageInput
  >(CreatePastorMessageDocument);

  const sendMessage = useCallback(
    async (input: MessageInput): Promise<MessageResult> => {
      const response = await executeMutation(input);

      if (response.error) {
        const message = extractGraphQLErrorMessage(
          response.error,
          "Не удалось отправить сообщение"
        );
        errorTracking.captureError(new Error(message), {
          tags: { feature: "pastor", action: "sendMessage" },
        });
        throw new Error(message);
      }

      if (!response.data?.createPastorMessage) {
        throw new Error("Не удалось отправить сообщение");
      }

      errorTracking.addBreadcrumb({
        category: "pastor",
        message: "Pastor message sent",
        data: { messageId: response.data.createPastorMessage.id },
      });

      return response.data.createPastorMessage;
    },
    [executeMutation]
  );

  return {
    sendMessage,
    isLoading: result.fetching,
    error: result.error,
  };
}
